import { onBeforeUnmount, onMounted } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'

// Recarregamentos disparados pela própria aplicação (o "Atualizar" da tela, o retorno de
// um modal de ação) não são o usuário abandonando o trabalho. Sem isto o navegador abre
// o diálogo dele, em inglês, para uma ação que ninguém entende como "sair da página" —
// e cancelar ali faz a tela nunca mostrar o que acabou de ser salvo.
let recarregamentoInterno = false

/**
 * Marca que o próximo unload é da aplicação e não deve ser questionado.
 * Chame imediatamente antes de `window.location.reload()`.
 */
export function allowNextReload() {
  recarregamentoInterno = true

  // Se o recarregamento não acontecer, a proteção volta a valer no mesmo tick
  setTimeout(() => {
    recarregamentoInterno = false
  }, 0)
}

/**
 * Avisa o usuário antes que ele perca o que digitou.
 *
 * Cobre os dois caminhos de saída de uma tela:
 * - navegação interna (menu, breadcrumb, botão Voltar do navegador), via guarda de rota,
 *   onde é possível mostrar um modal próprio e cancelar a saída;
 * - recarregar ou fechar a aba, via `beforeunload`, onde o navegador exibe o próprio
 *   diálogo e não permite personalizar o texto.
 *
 * @param {Object} options
 * @param {() => boolean} options.hasUnsavedChanges
 *        Consultada a cada tentativa de saída. Deve devolver `false` quando não há nada
 *        a proteger — inclusive quando a guarda estiver desligada.
 * @param {() => Promise<boolean>} options.confirmLeave
 *        Pergunta ao usuário se pode sair. Resolve `true` para sair descartando as
 *        alterações e `false` para permanecer na tela.
 *
 * @example
 * useUnsavedChangesGuard({
 *   hasUnsavedChanges: () => formularioAlterado(),
 *   confirmLeave: () => modal.value.confirmar()
 * })
 */
export function useUnsavedChangesGuard({ hasUnsavedChanges, confirmLeave }) {
  const aoSairDaPagina = (event) => {
    if (recarregamentoInterno || !hasUnsavedChanges()) {
      return
    }

    // Os navegadores ignoram mensagens personalizadas aqui; o que importa é
    // cancelar o evento para que o diálogo padrão apareça.
    event.preventDefault()
    event.returnValue = ''
  }

  onMounted(() => {
    window.addEventListener('beforeunload', aoSairDaPagina)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', aoSairDaPagina)
  })

  onBeforeRouteLeave(async () => {
    if (!hasUnsavedChanges()) {
      return true
    }

    return await confirmLeave()
  })
}
